import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  fetchStudentsAsync, 
  clearError, 
  setFilters, 
  clearFilters
} from '../store/slices/studentsSlice';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import LoadingSpinner from './LoadingSpinner';
import Tooltip from './Tooltip';
import { useLevels } from '../hooks/useLevels';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './GroupForm.css';

const Students: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { students, loading, error, pagination, filters } = useAppSelector((state) => state.students);
  const { groups } = useAppSelector((state) => state.groups);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(filters.groupId?.toString() || '');
  const [selectedLevel, setSelectedLevel] = useState<string>(filters.level || '');
  const [selectedStatus, setSelectedStatus] = useState<string>(filters.status || '');
  const initialLoadRef = useRef(false);

  // Canonical level list, derived server-side from the Group enum — keeps
  // a0/a1+/a2+/b1+ in sync instead of a hardcoded subset.
  const levels = useLevels();

  // Load groups only once on mount
  useEffect(() => {
    if (!initialLoadRef.current) {
      dispatch(fetchGroupsAsync({ page: 1, limit: 100 }));
      initialLoadRef.current = true;
    }
  }, [dispatch]);

  // Load students when filters or pagination change
  const loadStudents = useCallback(() => {
    dispatch(fetchStudentsAsync({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      search: filters.search,
      groupId: filters.groupId,
      level: filters.level,
      status: filters.status as 'active' | 'quitted' | undefined
    }));
  }, [dispatch, pagination.currentPage, pagination.itemsPerPage, filters.search, filters.groupId, filters.level, filters.status]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const handleSearch = useCallback(() => {
    dispatch(setFilters({ 
      search: searchInput.trim(),
      groupId: selectedGroupId ? parseInt(selectedGroupId) : undefined,
      level: selectedLevel || undefined,
      status: selectedStatus ? (selectedStatus as 'active' | 'quitted') : undefined
    }));
  }, [dispatch, searchInput, selectedGroupId, selectedLevel, selectedStatus]);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setSelectedGroupId('');
    setSelectedLevel('');
    setSelectedStatus('');
    dispatch(clearFilters());
  }, [dispatch]);

  const handlePageChange = useCallback((page: number) => {
    // Use the existing loadStudents function logic, but with different page
    dispatch(fetchStudentsAsync({
      page,
      limit: pagination.itemsPerPage,
      search: filters.search,
      groupId: filters.groupId,
      level: filters.level,
      status: filters.status as 'active' | 'quitted' | undefined
    }));
  }, [dispatch, pagination.itemsPerPage, filters.search, filters.groupId, filters.level, filters.status]);

  const formatDate = useCallback((dateString: string) => {
    return formatDateEuropean(dateString);
  }, []);

  if (loading && students.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Students</h1>
          <p>Manage student information and enrollment</p>
        </div>
        <button 
          onClick={() => navigate('/students/create')}
          className="btn-primary"
        >
          Add Student
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="filter-bar">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name..."
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />

        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          className="form-select"
        >
          <option value="">All Groups</option>
          {groups.map(group => (
            <option key={group.id} value={group.id}>
              {group.name || `${group.level.toUpperCase()} Group`}
            </option>
          ))}
        </select>

        <select
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(e.target.value)}
          className="form-select"
        >
          <option value="">All Levels</option>
          {levels.map(level => (
            <option key={level.value} value={level.value}>
              {level.label}
            </option>
          ))}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="form-select"
        >
          <option value="">Any Status</option>
          <option value="active">Active</option>
          <option value="quitted">Quitted</option>
        </select>

        <button onClick={handleSearch} className="btn-primary">
          Search
        </button>

        <button onClick={handleClearFilters} className="btn-secondary">
          Clear
        </button>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {students.length} of {pagination.totalItems} students
          {filters.search && ` matching "${filters.search}"`}
          {filters.level && ` at ${filters.level.toUpperCase()} level`}
          {filters.status && ` (${filters.status === 'active' ? 'Active' : 'Quitted'})`}
          {filters.groupId && (
            <>
              {' '}in{' '}
              {groups.find(g => g.id === filters.groupId)?.name || 
               `${groups.find(g => g.id === filters.groupId)?.level.toUpperCase()} Group`}
            </>
          )}
        </p>
      </div>

      {/* Students Table */}
      <div className="students-table-container">
        {students.length === 0 ? (
          <div className="no-students">
            <p>No students found.</p>
            {(filters.search || filters.groupId || filters.level) && (
              <button onClick={handleClearFilters} className="btn-primary">
                Clear filters to see all students
              </button>
            )}
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Level</th>
                <th>Group</th>
                <th>First Payment Date</th>
                <th>Sub Expiry</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <tr key={student.id}>
                  <td>
                    {student.atRisk && (
                      <Tooltip
                        text={`At risk (${student.riskLevel === 'certain' ? 'certain' : 'moderate'})${student.riskPotentialEndDate ? ` — est. leave ${formatDate(student.riskPotentialEndDate)}` : ''}`}
                      >
                        <span
                          className={`risk-dot risk-dot--${student.riskLevel === 'certain' ? 'certain' : 'moderate'}`}
                          aria-label="At risk"
                        />
                      </Tooltip>
                    )}
                    {student.firstName} {student.lastName} {student.surname}
                  </td>
                  <td>
                    {student.groups && student.groups.length > 0 ? student.groups[0].level.toUpperCase() : "-"}
                  </td>
                  <td>
                    {student.groups && student.groups.length > 0 ? (
                      student.groups[0].name || `Группа ${student.groups[0].level.toUpperCase()}`
                    ) : (
                      "No Group"
                    )}
                  </td>
                  <td>
                    {formatDate(student.startDate)}
                  </td>
                  <td>
                    {student.subExpEstimate ? formatDate(student.subExpEstimate) : '—'}
                  </td>
                  <td>
                    {student.quitDate && student.quitDate.split('T')[0] <= new Date().toISOString().split('T')[0] ? (
                      `Quit (${formatDate(student.quitDate)})`
                    ) : student.quitDate ? (
                      `Active · leaving ${formatDate(student.quitDate)}`
                    ) : (
                      "Active"
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => navigate(`/students/edit/${student.id}`)}
                        className="btn-icon"
                        data-tooltip="Edit student"
                      >
                        ✎
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || loading}
            className="btn-secondary"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            <span>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
          </div>
          
          <button 
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages || loading}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {loading && students.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default Students; 